import { useEffect, useState } from 'react';

import { keyframes } from '@emotion/react';
import styled from '@emotion/styled';
import { colors } from '@modernanimal/common';
import Modal, { defaultModalStyles } from '@modernanimal/common/src/web/atoms/Modal';

import Fonts, { fontFamily } from '../fonts';
import { DOWNLOAD_APP_LINK } from '../helpers';
import images from '../images';

export type Props = {
    isVisible: boolean;
    patientName?: string;
};

const fadeInOut = keyframes`
    0%, 100% { opacity: 0; }
    20%, 80% { opacity: 1; }
`;

const LoadingModal = ({ isVisible, patientName }: Props) => {
    const [currentTipIndex, setCurrentTipIndex] = useState(0);

    const tips = [
        {
            text: 'Be sure to add other humans to your household, so they can access your pet\'s profile and approve care.'
        },
        {
            text: 'Our team can always upload medical records on your behalf—just email them to ',
            link: {
                text: 'hello@modernanimal.com',
                url: 'mailto:hello@modernanimal.com'
            }
        },
        {
            text: 'Download the app to easily access all of your pet\'s health records and book appointments',
            link: {
                text: 'Download app',
                url: DOWNLOAD_APP_LINK
            }
        }
    ];

    useEffect(() => {
        if (!isVisible) return undefined;

        const interval = setInterval(() => {
            setCurrentTipIndex((prev) => (prev + 1) % tips.length);
        }, 3000);

        return () => {
            clearInterval(interval);
        };
    }, [isVisible, tips.length]);

    const currentTip = tips[currentTipIndex];

    return (
        <Modal
            isModalOpen={isVisible}
            onCloseModal={() => { }}
            shouldCloseOnOverlayClick={false}
            customStyles={{
                overlay: overlayStyles,
                content: contentStyles
            }}
        >
            <ResponsiveModalWrapper>
                <LoadingModalContainer>
                    <ImageSection>
                        <MarchingAnimalsImage src={images.MarchingAnimals} alt="Marching animals" />
                        <TextSection>
                            <LoadingTitle>
                                Hang tight while we create
                                {' '}
                                {patientName ?? 'your pet'}
                                &apos;s profile...
                            </LoadingTitle>
                            <LoadingSubtitle>
                                This may take a few moments.
                            </LoadingSubtitle>
                        </TextSection>
                    </ImageSection>
                    <TipsSection>
                        <TipsHeader>Did you know?</TipsHeader>
                        <TipText>
                            {currentTip.text}
                            {currentTip.link && (
                                <>
                                    {' '}
                                    <TipLink
                                        href={currentTip.link.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        {currentTip.link.text}
                                    </TipLink>
                                </>
                            )}
                        </TipText>
                    </TipsSection>
                </LoadingModalContainer>
            </ResponsiveModalWrapper>
        </Modal>
    );
};

export default LoadingModal;

const overlayStyles = {
    ...defaultModalStyles.overlay,
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    backdropFilter: 'blur(4px)',
};

const contentStyles = {
    ...defaultModalStyles.content,
    borderRadius: '24px',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15), 0 8px 32px rgba(0, 0, 0, 0.1)',
    border: 'none',
    maxWidth: '95vw',
    maxHeight: '95vh',
    width: '340px',
    height: 'auto',
    padding: '0',
    overflow: 'hidden',
};

const ResponsiveModalWrapper = styled.div`
    width: 100%;
    height: 100%;
    
    @media (max-width: 480px) {
        .ReactModal__Content {
            width: 90vw !important;
            max-height: 90vh !important;
            border-radius: 16px !important;
        }
    }
`;

const LoadingModalContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: space-between;
    min-height: 500px;
    padding: 24px 20px 20px;
    background: linear-gradient(135deg, ${colors.WHITE} 0%, #fafafa 100%);
    gap: 24px;
    position: relative;
    border-radius: 24px;

    @media (max-width: 480px) {
        min-height: 450px;
        padding: 20px 16px 16px;
        gap: 20px;
        border-radius: 16px;
    }
`;

const ImageSection = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 24px;
    flex: 1;
    justify-content: center;
    margin-top: 40px;

    @media (max-width: 480px) {
        margin-top: 32px;
    }
`;

const MarchingAnimalsImage = styled.img`
    width: 180px;
    height: 165px;
    object-fit: contain;

    @media (max-width: 480px) {
        width: 150px;
        height: 138px;
    }
`;

const TextSection = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
    text-align: center;
`;

const LoadingTitle = styled(Fonts.Heading04)`
    text-align: center;
    margin: 0;
`;

const LoadingSubtitle = styled(Fonts.Body)`
    color: ${colors.GREY_2};
    text-align: center;
    margin: 0;
`;

const TipsSection = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
    text-align: center;
    max-width: 300px;
    padding: 16px;
    background: rgba(248, 249, 250, 0.8);
    border-radius: 16px;
    border: 1px solid rgba(0, 0, 0, 0.05);
    backdrop-filter: blur(8px);

    @media (max-width: 480px) {
        max-width: 280px;
        padding: 12px;
        gap: 8px;
    }
`;

const TipsHeader = styled.div`
    text-align: center;
    margin: 0;
    font-family: ${fontFamily.baseMedium};
    font-size: 14px;
    line-height: 18px;
    color: ${colors.GREY_1};
    text-transform: uppercase;
    letter-spacing: 0.5px;
    font-weight: 600;
`;

const TipText = styled(Fonts.Body)`
    color: ${colors.GREY_2};
    text-align: center;
    margin: 0;
    animation: ${fadeInOut} 3s ease-in-out infinite;
`;

const TipLink = styled.a`
    color: ${colors.PRIMARY_PERIWINKLE};
    text-decoration: underline;
    cursor: pointer;

    &:hover {
        text-decoration: none;
    }
`;
